// The network's storage cells, read through transposers against the ME Drives.

/** One cell in a drive slot. Fluid and essentia cells are listed but can't be measured. */
export interface StorageCell {
  /** 1-based slot in the drive. */
  slot: number;
  name: string;
  damage: number;
  label: string;
  readable: boolean;
  totalBytes: number | null;
  usedBytes: number | null;
  totalTypes: number | null;
  usedTypes: number | null;
  /** Items stored. */
  count: number | null;
}

/** An ME Drive or ME Chest a transposer touches. */
export interface StorageDrive {
  /** Transposer address and the side (0 to 5) the drive is on: names the drive. */
  transposer: string;
  side: number;
  inventory: string;
  slots: number;
  cells: StorageCell[];
}

export interface StorageTotals {
  drives: number;
  cells: number;
  /** Cells whose fill can't be read (fluid and essentia cells). */
  unreadable: number;
  totalBytes: number;
  usedBytes: number;
  totalTypes: number;
  usedTypes: number;
  /** Readable cells with at least 90% of their bytes or types used. */
  nearlyFull: number;
  /** Empty slots across the drives. */
  freeSlots: number;
}

export interface StorageRead {
  at: number;
  transposers: number;
  drives: StorageDrive[];
  totals: StorageTotals;
}

/** One stored sample of the totals, for the trend. */
export interface StorageSample {
  at: number;
  usedBytes: number;
  totalBytes: number;
  usedTypes: number;
  totalTypes: number;
}

export interface StorageView {
  /** The last read, kept across restarts. Null before the first one. */
  last: StorageRead | null;
  loading: boolean;
  error: string | null;
  /** Why storage can't be read right now (no game link, Butler too old), or null. */
  unavailable: string | null;
  /** Bytes added per day over the last week, from the samples. Null with too few samples. */
  bytesPerDay: number | null;
  typesPerDay: number | null;
  /** When the bytes or the types run out at that rate. Null when not growing. */
  fullAt: number | null;
  samples: StorageSample[];
}

/** Share of a cell or total used, from 0 to 1, by bytes or by types, whichever is fuller. */
export function fillOf(used: { usedBytes: number | null; totalBytes: number | null; usedTypes: number | null; totalTypes: number | null }): number {
  const b = used.totalBytes ? (used.usedBytes ?? 0) / used.totalBytes : 0;
  const t = used.totalTypes ? (used.usedTypes ?? 0) / used.totalTypes : 0;
  return Math.max(b, t);
}

export function storageTotals(drives: readonly StorageDrive[]): StorageTotals {
  const t: StorageTotals = { drives: drives.length, cells: 0, unreadable: 0, totalBytes: 0, usedBytes: 0, totalTypes: 0, usedTypes: 0, nearlyFull: 0, freeSlots: 0 };
  for (const d of drives) {
    t.freeSlots += Math.max(0, d.slots - d.cells.length);
    for (const c of d.cells) {
      t.cells++;
      if (!c.readable) {
        t.unreadable++;
        continue;
      }
      t.totalBytes += c.totalBytes ?? 0;
      t.usedBytes += c.usedBytes ?? 0;
      t.totalTypes += c.totalTypes ?? 0;
      t.usedTypes += c.usedTypes ?? 0;
      if (fillOf(c) >= 0.9) t.nearlyFull++;
    }
  }
  return t;
}
